import {
    type Integration,
    type IntegrationInstallation,
    IntegrationInstallationSiteSelection,
    IntegrationInstallationSpaceSelection,
} from '@gitbook/api-client';
import { Button, StepControls, toast, useRadioGroupContext } from '@gitbook/spine-react';

import {
    installIntegrationInSites,
    installIntegrationInSpaces,
    updateIntegrationInstallationSiteSelection,
    updateIntegrationInstallationSpaceSelection,
} from '../../../integrations.api';
import type { StepContextType } from '../../types';

/**
 * Controls for the installation step
 */
export function InstallControls(props: {
    organizationId: string;
    integration: Integration;
    installation: IntegrationInstallation;
    selectedSpaceIds: string[];
    selectedSiteIds: string[];
    onSuccessInstallInContent: (params: StepContextType<'pick_content_to_configure'>) => void;
    onSuccessInstallInAllContent: () => void;
}) {
    const { organizationId, integration, installation, selectedSiteIds, selectedSpaceIds } = props;

    const radioContext = useRadioGroupContext();
    const { selectedValue } = radioContext;

    const buttonDisabled = (() => {
        if (!selectedValue) {
            return true;
        }

        if (selectedValue === 'site_selected' && selectedSiteIds.length === 0) {
            return true;
        }

        if (selectedValue === 'space_selected' && selectedSpaceIds.length === 0) {
            return true;
        }
    })();

    return (
        <StepControls>
            <Button
                label="Install"
                kind="primary"
                disabled={buttonDisabled}
                onPress={async () => {
                    await toast.catch(async () => {
                        switch (selectedValue) {
                            case 'space_selected':
                            case 'site_selected': {
                                const newInstallations = await installIntegrationInContent({
                                    installation,
                                    integrationName: integration.name,
                                    organizationId,
                                    contentIds:
                                        selectedValue === 'site_selected'
                                            ? selectedSiteIds
                                            : selectedSpaceIds,
                                    type: selectedValue === 'site_selected' ? 'site' : 'space',
                                });

                                props.onSuccessInstallInContent(newInstallations);
                                break;
                            }
                            case 'site_all': {
                                await updateIntegrationInstallationSiteSelection({
                                    installation,
                                    integrationName: integration.name,
                                    organizationId,
                                    selection: IntegrationInstallationSiteSelection.All,
                                });

                                props.onSuccessInstallInAllContent();
                                break;
                            }
                            case 'space_all': {
                                await updateIntegrationInstallationSpaceSelection({
                                    installation,
                                    integrationName: integration.name,
                                    organizationId,
                                    selection: IntegrationInstallationSpaceSelection.All,
                                });

                                props.onSuccessInstallInAllContent();
                                break;
                            }
                            case null:
                                break;
                        }
                    }, 'Something whent wrong while installing the integration in content');
                }}
            />
        </StepControls>
    );
}

async function installIntegrationInContent(params: {
    installation: IntegrationInstallation;
    integrationName: string;
    contentIds: string[];
    organizationId: string;
    type: 'site' | 'space';
}) {
    const { installation, integrationName, contentIds, organizationId, type } = params;
    switch (type) {
        case 'site':
            return {
                type: 'site',
                installations: await installIntegrationInSites({
                    installation,
                    integrationName,
                    siteIds: contentIds,
                    organizationId,
                }),
            } as const;
        case 'space':
            return {
                type: 'space',
                installations: await installIntegrationInSpaces({
                    installation,
                    integrationName,
                    spaceIds: contentIds,
                    organizationId,
                }),
            } as const;
    }
}
